import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Cast, Tv, Play, Pause, Plus, Check, Wifi, WifiOff, X, ChevronDown,
  ChevronUp, ChevronLeft, ChevronRight,
  Volume1, Volume2, VolumeX, Home, ArrowLeft, Power, MonitorPlay,
} from 'lucide-react';
import { PLAY_BUTTON_GRADIENT } from '../lib/theme';
import VizioPairingPrompt from '../components/VizioPairingPrompt';
import VizioPairingModal from '../components/VizioPairingModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SVC = {
  netflix:        { name: 'Netflix',      color: '#E50914' },
  hulu:           { name: 'Hulu',         color: '#1CE783' },
  disney_plus:    { name: 'Disney+',      color: '#113CCF' },
  hbo_max:        { name: 'Max',          color: '#B535F6' },
  amazon_prime:   { name: 'Prime Video',  color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',    color: '#bbbbbb' },
  peacock:        { name: 'Peacock',      color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',   color: '#0064FF' },
};
const svc = id => SVC[id] || { name: id || '···', color: '#555' };

// Roku ECP app IDs for quick-launch shortcuts
const ROKU_APP_IDS = {
  netflix: '12', hulu: '2285', disney_plus: '3201',
  hbo_max: '34399', amazon_prime: '13', apple_tv: '8378',
  peacock: '593099', paramount_plus: '31440',
};

// Roku ECP key names (POST /keypress/{key}). Power is handled separately
// via QudeBridge.sendRokuPower(), which checks device-info power-mode first
// and sends PowerOn or PowerOff accordingly -- ECP treats those as two
// distinct keys, not a toggle, so a hardcoded PowerOff here could never
// turn the TV back on from standby.
const ROKU_KEYS = {
  up: 'Up', down: 'Down', left: 'Left', right: 'Right', ok: 'Select',
  back: 'Back', home: 'Home', vol_up: 'VolumeUp', vol_dn: 'VolumeDown',
  mute: 'VolumeMute', play: 'Play', pause: 'Pause',
};

// Vizio SmartCast key codes [CODESET, CODE] — from the SmartCast API spec
// CODESET 3 = navigation, 4 = nav extras, 5 = volume, 2 = playback, 11 = power
const VIZIO_KEYS = {
  up: [3, 8], down: [3, 0], left: [3, 1], right: [3, 7], ok: [3, 2],
  back: [4, 0], home: [4, 3],
  vol_up: [5, 1], vol_dn: [5, 0], mute: [5, 3],
  power: [11, 2], play: [2, 3], pause: [2, 2],
};

// Vizio SmartCast app launch payloads (NAME_SPACE + APP_ID from SmartCast spec).
// Only include apps whose IDs are reliably documented — hide others for Vizio.
const VIZIO_APP_IDS = {
  netflix:      { nameSpace: 3, appId: '1'  },
  hulu:         { nameSpace: 3, appId: '7'  },
  disney_plus:  { nameSpace: 3, appId: '12' },
  amazon_prime: { nameSpace: 3, appId: '3'  },
};

// Logo paths (assets already in /public) and tile backgrounds per service
const LOGO = {
  netflix:        { src: '/netflix.png',        bg: '#141414' },
  hulu:           { src: '/hulu.svg',           bg: '#0d0d0d' },
  disney_plus:    { src: '/disney-plus.svg',    bg: '#0a1444' },
  hbo_max:        { src: '/max.svg',            bg: '#040404' },
  amazon_prime:   { src: '/prime-video.svg',    bg: '#0d1117' },
  apple_tv:       { src: '/apple-tv.png',       bg: '#1a1a1a' },
  peacock:        { src: '/peacock.svg',         bg: '#0a0a0a' },
  paramount_plus: { src: '/paramount-plus.svg', bg: '#00112e' },
};

// Quick-launch apps shown in remote panel
const QUICK_APPS = [
  { id: 'netflix',        name: 'Netflix',    color: '#E50914' },
  { id: 'hulu',           name: 'Hulu',       color: '#1CE783' },
  { id: 'disney_plus',    name: 'Disney+',    color: '#113CCF' },
  { id: 'hbo_max',        name: 'Max',        color: '#B535F6' },
  { id: 'amazon_prime',   name: 'Prime',      color: '#00A8E1' },
  { id: 'apple_tv',       name: 'Apple TV+',  color: '#cccccc' },
  { id: 'peacock',        name: 'Peacock',    color: '#FDB927' },
  { id: 'paramount_plus', name: 'Paramount+', color: '#0064FF' },
];

const DEVICE_ICON = {
  roku: '📺', firetv: '🔥', samsung: '📺', lg: '📺',
  vizio: '📺', chromecast: '🎬', appletv: '🍎',
};
const deviceIcon = type => DEVICE_ICON[(type || '').toLowerCase()] || '📺';

function isOnline(isoStr) {
  return isoStr && Date.now() - new Date(isoStr).getTime() < 5 * 60_000;
}

function epLabel(s, e) {
  if (s && e) return `S${s}E${e}`;
  if (s) return `S${s}`;
  return null;
}

function getPlayUrl(serviceId, title) {
  const q = encodeURIComponent(title || '');
  return ({
    netflix:        `https://www.netflix.com/search?q=${q}`,
    hulu:           `https://www.hulu.com/search?q=${q}`,
    disney_plus:    `https://www.disneyplus.com/search?q=${q}`,
    hbo_max:        `https://play.max.com/search?q=${q}`,
    amazon_prime:   `https://www.amazon.com/s?k=${q}&i=instant-video`,
    apple_tv:       `https://tv.apple.com/search?term=${q}`,
    peacock:        `https://www.peacocktv.com/search?q=${q}`,
    paramount_plus: `https://www.paramountplus.com/search?q=${q}`,
  })[serviceId] || null;
}

// Send a key to the selected device via the JS bridge (or ECP fetch fallback)
function bridgeSendKey(device, key) {
  if (!device) return;

  if (device.platform === 'roku') {
    const ip = device.ip;
    const rokuKey = ROKU_KEYS[key] || key;

    if (ip) {
      // Route through the native bridge, not a WebView fetch(). fetch() to a
      // LAN IP from this HTTPS page has no timeout of its own — if the IP is
      // stale or briefly unreachable it can stall for several seconds before
      // failing, which is exactly the "7-second button delay" this app was
      // shipping. QudeBridge.sendRokuKey() fires keydown+keyup on a background
      // thread with a 500ms timeout and returns immediately — it never blocks
      // this JS call, so one press = one action, fast, or a fast failure.
      if (key === 'power' && window.QudeAndroid?.sendRokuPower) {
        // State-aware: checks the TV's current power-mode and sends
        // PowerOn or PowerOff accordingly, so this button can actually
        // wake the TV from standby, not just put it to sleep.
        window.QudeAndroid.sendRokuPower(ip);
      } else if (key === 'qude_home' && window.QudeAndroid?.sendQudeHome) {
        // Dedicated, unconditional "go to Qude's own Hub" — no detection,
        // no fallback to exiting the app. Separate from the regular Home
        // button below, which still exits to the Roku system Home screen.
        window.QudeAndroid.sendQudeHome(ip);
      } else if (rokuKey === 'Home' && window.QudeAndroid?.sendHomeSmart) {
        // Home is special-cased: if Qude's own channel is the active app,
        // this navigates the channel to its Hub view instead of exiting to
        // the Roku system Home screen (which would kick the user out of
        // Qude entirely). Falls back to a normal Home keypress if some
        // other app is active. See QudeBridge.sendHomeSmart's docstring.
        window.QudeAndroid.sendHomeSmart(ip);
      } else if (window.QudeAndroid?.sendRokuKey) {
        window.QudeAndroid.sendRokuKey(ip, rokuKey);
      } else {
        toast.error('Remote needs Android app', { duration: 2000 });
      }
    } else {
      toast.error('No IP for this Roku — ensure it\'s on the same Wi-Fi', { duration: 2500 });
    }
    return;
  }

  if (device.platform === 'vizio') {
    const ip = device.ip;
    if (ip && window.QudeAndroid?.sendVizioKey) {
      const [cs, c] = VIZIO_KEYS[key] || [0, 0];
      const result = window.QudeAndroid.sendVizioKey(ip, cs, c);
      toast.success(`→ Vizio ${ip} / ${key} → ${result ?? 'sent'}`, { duration: 1500 });
    } else if (!ip) {
      toast.error('No IP for this Vizio — ensure it\'s on the same Wi-Fi', { duration: 2500 });
    } else {
      toast.error('Remote needs Android app', { duration: 2000 });
    }
    return;
  }

  if (!window.QudeAndroid) {
    toast.error('Remote needs Android app', { duration: 2000 });
  }
}

function loadCachedDevices() {
  try { return JSON.parse(localStorage.getItem('qude_paired_devices') || '[]'); } catch { return []; }
}

export default function Remote() {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  // Initialize from localStorage cache so paired devices show instantly on re-open
  const [devices,        setDevices]        = useState(loadCachedDevices);
  const [rokuDevices,    setRokuDevices]    = useState([]);   // LAN-discovered Rokus
  const [pairedVizios,   setPairedVizios]   = useState([]);   // LAN paired Vizios
  const [selectedDevice, setSelectedDevice] = useState(null); // unified device object
  const [hub,            setHub]            = useState(null);
  // If we have a cache, don't block on loading — show the remote right away
  const [loading,        setLoading]        = useState(() => loadCachedDevices().length === 0);
  const [launching,      setLaunching]      = useState(null);
  const [showPair,       setShowPair]       = useState(false);
  const [pairCode,       setPairCode]       = useState('');
  const [claiming,       setClaiming]       = useState(false);
  const [showPlatforms,  setShowPlatforms]  = useState(false);
  const [unpairedVizios, setUnpairedVizios] = useState([]);
  const [vizioModalTarget, setVizioModalTarget] = useState(null);

  // The Roku actually running Qude, verified — not just "whichever
  // discovered Roku answered first". A household with more than one
  // Roku-based TV made that assumption wrong, and was the cause of both
  // "No IP for this Roku" (paired device got no IP, or the wrong one,
  // injected) and launches silently targeting the wrong screen.
  // resolveQudeRokuIp checks each discovered candidate's installed apps
  // for the sideloaded Qude channel.
  const [resolvedQudeRokuIp, setResolvedQudeRokuIp] = useState('');
  const resolveQudeIp = useCallback(() => {
    if (!window.QudeAndroid?.resolveQudeRokuIp) return;
    try { setResolvedQudeRokuIp(window.QudeAndroid.resolveQudeRokuIp() || ''); } catch {}
  }, []);

  // Unified device list: Roku (local) + Vizio (local) + protocol devices (cloud)
  const allDevices = useMemo(() => {
    const out = [];
    rokuDevices.forEach(d => out.push({
      id: `roku_${d.ip}`, name: d.name || 'Roku TV',
      platform: 'roku', ip: d.ip, device_id: null,
    }));
    pairedVizios.forEach(v => out.push({
      id: `vizio_${v.ip}`, name: v.name || 'Vizio TV',
      platform: 'vizio', ip: v.ip, device_id: null,
    }));
    devices.forEach(d => {
      const platform = (d.device_type || '').toLowerCase();
      // Inject the verified Qude-Roku IP, not just any discovered Roku.
      let ip = null;
      if (platform === 'roku' && resolvedQudeRokuIp) {
        ip = resolvedQudeRokuIp;
      }
      // Skip if this protocol device is already represented by a LAN entry
      // (same platform + same IP) to avoid duplicate entries in the picker.
      const alreadyListed = ip && out.some(o => o.platform === platform && o.ip === ip && !o.device_id);
      if (!alreadyListed) {
        out.push({
          id: d.device_id, name: d.device_name || d.device_type,
          platform, ip,
          device_id: d.device_id, last_seen_at: d.last_seen_at,
        });
      }
    });
    return out;
  }, [rokuDevices, pairedVizios, devices, resolvedQudeRokuIp]);

  // Auto-select: prefer a device that has both device_id (protocol) and ip (LAN),
  // otherwise fall back to allDevices[0].
  useEffect(() => {
    if (selectedDevice || !allDevices.length) return;
    const best = allDevices.find(d => d.device_id && d.ip) || allDevices[0];
    setSelectedDevice(best);
  }, [allDevices, selectedDevice]);

  const refreshVizioStatus = useCallback(() => {
    if (!window.QudeAndroid?.getKnownVizios) return;
    try {
      const raw = window.QudeAndroid.getKnownVizios() || '[]';
      const list = JSON.parse(raw);
      const dismissed = JSON.parse(localStorage.getItem('vizio_pairing_dismissed_ips') || '[]');
      setPairedVizios(list.filter(tv => tv?.ip && tv.paired));
      setUnpairedVizios(list.filter(tv => tv?.ip && !tv.paired && !dismissed.includes(tv.ip)));
    } catch {}
  }, []);

  const refreshRokuDevices = useCallback(() => {
    if (!window.QudeAndroid?.getDiscoveredRokus) return;
    try {
      const raw = window.QudeAndroid.getDiscoveredRokus() || '[]';
      const list = JSON.parse(raw);
      const ignored = JSON.parse(localStorage.getItem('qude_ignored_roku_ips') || '[]');
      if (list.length) setRokuDevices(list.filter(d => !ignored.includes(d.ip)));
    } catch {}
  }, []);

  // A household can have more than one Roku-based TV (e.g. a second Roku-OS
  // smart TV) — LAN discovery has no way to know which one is actually
  // running the Qude channel, so it shows up in the picker with no clean way
  // to dismiss it (the existing "Remove" action only applied to
  // protocol-paired devices, which have a device_id; a plain LAN-discovered
  // Roku doesn't). This persists a per-IP ignore list, same pattern as the
  // existing Vizio dismiss flow.
  const ignoreRoku = useCallback((ip) => {
    try {
      const ignored = JSON.parse(localStorage.getItem('qude_ignored_roku_ips') || '[]');
      if (!ignored.includes(ip)) {
        ignored.push(ip);
        localStorage.setItem('qude_ignored_roku_ips', JSON.stringify(ignored));
      }
    } catch {}
    setRokuDevices(prev => prev.filter(d => d.ip !== ip));
    setSelectedDevice(prev => (prev?.ip === ip ? null : prev));
  }, []);

  // Proactively notice a Qude Roku on the network that isn't paired yet,
  // instead of requiring the user to already know to tap "Pair TV" and dig
  // for a code on the TV themselves. This can't skip the code entirely —
  // full zero-touch auto-pair would mean any phone on the same Wi-Fi could
  // silently claim someone else's TV — but surfacing "we found it, here's
  // where to enter the code" is a real improvement over nothing at all.
  const [dismissedUnpairedRoku, setDismissedUnpairedRoku] = useState(false);
  const unpairedQudeRokuIp = useMemo(() => {
    if (!resolvedQudeRokuIp) return null;
    const alreadyPaired = devices.some(d => (d.device_type || '').toLowerCase() === 'roku');
    return alreadyPaired ? null : resolvedQudeRokuIp;
  }, [resolvedQudeRokuIp, devices]);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const h = { Authorization: `Bearer ${token}` };
      const [devRes, hubRes] = await Promise.all([
        axios.get(`${API}/qude-protocol/device/list`, { headers: h }),
        axios.get(`${API}/hub`, { headers: h }).catch(() => ({ data: null })),
      ]);
      const freshDevices = devRes.data?.devices || [];
      setDevices(freshDevices);
      // Persist so the Remote opens immediately with known devices on re-visit
      try { localStorage.setItem('qude_paired_devices', JSON.stringify(freshDevices)); } catch {}
      setHub(hubRes.data);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!user) return;
    load();
    refreshVizioStatus();
    // Kick off Roku LAN discovery; read results after a short delay
    if (window.QudeAndroid?.discoverRokus) window.QudeAndroid.discoverRokus();
    const timers = [
      setTimeout(refreshVizioStatus, 3_000),
      setTimeout(refreshVizioStatus, 8_000),
      setTimeout(refreshVizioStatus, 15_000),
      setTimeout(refreshRokuDevices, 5_000),   // discovery usually finishes in ~4 s
      setTimeout(refreshRokuDevices, 10_000),
      // resolveQudeRokuIp does its own network round-trips per candidate, so
      // give discovery a head start before checking.
      setTimeout(resolveQudeIp, 6_000),
      setTimeout(resolveQudeIp, 12_000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [user, load, refreshVizioStatus, refreshRokuDevices, resolveQudeIp]);

  // §patent-claim: remote origination detection — launching an app or content
  // via the remote surface is high-confidence evidence of viewing intent (§claim-origination)
  const sendOriginationSignal = useCallback(async (service, title, season, episode) => {
    if (!service && !title) return;
    try {
      const token = await getToken();
      await axios.post(`${API}/detection/signal`, {
        source: 'remote_origination',
        service: service || null,
        title: title || null,
        media_type: 'tv',
        season: season || null,
        episode: episode || null,
        confidence: 0.9,
        state: 'playing',
        observed_at: new Date().toISOString(),
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
  }, [getToken]);

  // App-only launch — no show context, no detection signal.
  // A no-title signal must never reach /api/detection/signal (contract: title required).
  const launchService = useCallback(async (serviceId) => {
    if (!selectedDevice) { toast.error('Select a TV first'); return; }
    if (selectedDevice.platform === 'roku' && selectedDevice.ip) {
      const appId = ROKU_APP_IDS[serviceId];
      if (appId && window.QudeAndroid?.launchRokuApp) {
        window.QudeAndroid.launchRokuApp(selectedDevice.ip, appId);
        toast.success(`Opening ${svc(serviceId).name} on ${selectedDevice.name}`);
      }
    } else if (selectedDevice.platform === 'vizio' && selectedDevice.ip) {
      const vizioApp = VIZIO_APP_IDS[serviceId];
      if (vizioApp && window.QudeAndroid?.launchVizioApp) {
        const result = window.QudeAndroid.launchVizioApp(selectedDevice.ip, vizioApp.nameSpace, vizioApp.appId);
        if (result === 'ok') {
          toast.success(`Opening ${svc(serviceId).name} on ${selectedDevice.name}`);
        } else {
          toast.error(`Could not launch ${svc(serviceId).name} (${result})`);
        }
      }
    } else if (selectedDevice.device_id) {
      try {
        const token = await getToken();
        await axios.post(`${API}/qude-protocol/launch`, {
          device_id: selectedDevice.device_id, service: serviceId,
          title: '', tmdb_id: '', deep_link: getPlayUrl(serviceId, '') || '',
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`Sent ${svc(serviceId).name} to ${selectedDevice.name}`);
      } catch { toast.error('Could not reach the TV'); }
    }
  }, [selectedDevice, getToken]);

  const launch = useCallback(async (card) => {
    if (!selectedDevice) { toast.error('Select a TV first'); return; }
    const key = card.show_key || card.title;
    setLaunching(key);
    const serviceId = card.service || card.service_id || '';
    const season  = card.next_season  || card.current_season;
    const episode = card.next_episode || card.current_episode;

    // §patent-claim: content-launch origination — title-level detection signal (§claim-origination)
    sendOriginationSignal(serviceId, card.title, season, episode);

    try {
      // Local Roku: open the service app (no content-ID deep-link available)
      if (selectedDevice.platform === 'roku' && selectedDevice.ip) {
        const appId = ROKU_APP_IDS[serviceId];
        if (appId && window.QudeAndroid?.launchRokuApp) {
          window.QudeAndroid.launchRokuApp(selectedDevice.ip, appId);
          toast.success(`▶ Opening ${svc(serviceId).name} on ${selectedDevice.name}`);
          return;
        }
      }
      // Local Vizio: deep-link via existing bridge helper
      if (selectedDevice.platform === 'vizio' && selectedDevice.ip && window.QudeAndroid?.launchOnLocalDevice) {
        const result = window.QudeAndroid.launchOnLocalDevice('vizio', selectedDevice.ip, getPlayUrl(serviceId, card.title) || '');
        if (result === 'ok') { toast.success(`▶ Opening on ${selectedDevice.name}`); return; }
      }
      // Cloud relay for protocol-paired devices (Roku channel, Fire TV, etc.)
      if (selectedDevice.device_id) {
        const token = await getToken();
        await axios.post(`${API}/qude-protocol/launch`, {
          device_id: selectedDevice.device_id,
          title:     card.title,
          tmdb_id:   card.tmdb_id || '',
          service:   serviceId,
          deep_link: getPlayUrl(serviceId, card.title) || '',
          season, episode,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`▶ Sent to ${selectedDevice.name}`);
      } else {
        toast.error('Could not reach the TV');
      }
    } catch {
      toast.error('Could not reach the TV');
    } finally {
      setLaunching(null);
    }
  }, [selectedDevice, getToken, sendOriginationSignal]);

  const claimDevice = useCallback(async () => {
    const code = pairCode.trim().toUpperCase();
    if (code.length !== 6) { toast.error('Enter the 6-character code shown on your TV'); return; }
    setClaiming(true);
    try {
      const token = await getToken();
      const res = await axios.post(
        `${API}/qude-protocol/pair/claim`,
        { pairing_code: code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${res.data.device_name} paired!`);
      setPairCode('');
      setShowPair(false);
      await load();
      // /pair/claim only marks the pairing "claimed" server-side — the
      // actual device record doesn't exist until the Roku's own poll
      // (every 5s, see PairingView.brs) notices and mints the token. A
      // single immediate load() here almost always runs before that's
      // happened, so the Remote page looked like pairing hadn't worked for
      // several seconds even though it had. Keep checking until it shows up.
      [3_000, 6_000, 10_000].forEach(ms => setTimeout(load, ms));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code not found or expired');
    } finally {
      setClaiming(false);
    }
  }, [pairCode, getToken, load]);

  const unpair = useCallback(async (dev) => {
    if (!dev.device_id) { toast.error('Local device — remove from TV settings'); return; }
    // This is a real, disruptive action (revokes the device server-side —
    // the TV will have to go through the pairing code again), not just
    // hiding an entry. With multiple similarly-named Roku entries in the
    // picker (a household with more than one Roku-based TV), an accidental
    // tap here on the wrong selected device was too easy and had no undo.
    if (!window.confirm(`Unpair ${dev.name}? You'll need to enter a new pairing code on the TV to reconnect.`)) return;
    try {
      const token = await getToken();
      await axios.post(
        `${API}/qude-protocol/device/${dev.device_id}/revoke`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${dev.name} removed`);
      if (selectedDevice?.id === dev.id) setSelectedDevice(null);
      await load();
    } catch {
      toast.error('Could not remove device');
    }
  }, [getToken, load, selectedDevice]);

  const _rawTonightPick = hub?.up_next?.[0] || hub?.resume;
  const tonightPick = _rawTonightPick?.title?.startsWith('http') ? null : _rawTonightPick;
  const shows = [
    tonightPick,
    ...(hub?.up_next?.slice(1) || []),
    ...(hub?.stale || []),
    ...(hub?.watchlist?.slice(0, 5) || []),
  ].filter(Boolean).filter(c => !c.title?.startsWith('http'));

  const hasLocalControl = selectedDevice &&
    (selectedDevice.platform === 'roku' || selectedDevice.platform === 'vizio');

  if (loading) return (
    <div className="flex items-center justify-center" style={{ minHeight: 300 }}>
      <div className="w-7 h-7 border-2 rounded-full animate-spin" style={{ borderColor: '#7c3aed', borderTopColor: 'transparent' }} />
    </div>
  );

  return (
    <div className="pb-24">

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-black" style={{ fontFamily: 'Outfit' }}>Remote</h1>
          <p className="text-xs text-neutral-500 mt-0.5">Control your TV &amp; send shows</p>
        </div>
        <button
          onClick={() => setShowPair(v => !v)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold border border-white/10 text-neutral-400 hover:text-white hover:border-white/20 active:scale-95 transition-all"
        >
          {showPair ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          {showPair ? 'Cancel' : 'Pair TV'}
        </button>
      </div>

      {/* Vizio auto-detected on LAN */}
      {unpairedVizios[0] && (
        <div className="mb-4">
          <VizioPairingPrompt
            tvName={unpairedVizios[0].name}
            ipAddress={unpairedVizios[0].ip}
            onPair={(ip, name) => setVizioModalTarget({ ipAddress: ip, tvName: name })}
            onDismiss={() => {
              try {
                const dismissed = JSON.parse(localStorage.getItem('vizio_pairing_dismissed_ips') || '[]');
                if (!dismissed.includes(unpairedVizios[0].ip)) dismissed.push(unpairedVizios[0].ip);
                localStorage.setItem('vizio_pairing_dismissed_ips', JSON.stringify(dismissed));
              } catch {}
              setUnpairedVizios(prev => prev.filter(tv => tv.ip !== unpairedVizios[0].ip));
            }}
          />
        </div>
      )}

      {/* Found a Qude Roku on the network that isn't paired yet */}
      {unpairedQudeRokuIp && !dismissedUnpairedRoku && !showPair && (
        <div className="mb-5 p-4 rounded-2xl border border-blue-500/30 bg-blue-950/20 flex items-start gap-3">
          <MonitorPlay className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">Found a Qude Roku on your network</p>
            <p className="text-xs text-neutral-400 mt-0.5">It isn't paired to this account yet — a code should be on screen.</p>
            <div className="flex gap-2 mt-2.5">
              <button onClick={() => setShowPair(true)}
                className="px-3 py-1.5 rounded-full text-xs font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' }}>
                Enter code
              </button>
              <button onClick={() => setDismissedUnpairedRoku(true)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold text-neutral-400 border border-white/10">
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vizio pairing modal */}
      {vizioModalTarget && (
        <VizioPairingModal
          ipAddress={vizioModalTarget.ipAddress}
          deviceName={vizioModalTarget.tvName}
          onDismiss={() => setVizioModalTarget(null)}
          onPaired={() => {
            setVizioModalTarget(null);
            setUnpairedVizios(prev => prev.filter(tv => tv.ip !== vizioModalTarget?.ipAddress));
            refreshVizioStatus();
            load();
          }}
        />
      )}

      {/* Pair form */}
      {showPair && (
        <div className="mb-5 p-4 rounded-2xl border border-violet-500/25 bg-violet-950/20">
          <p className="text-sm font-semibold text-white mb-0.5">Enter the code on your TV</p>
          <p className="text-xs text-neutral-500 mb-3">
            Open the Qude channel on your Roku or Fire TV — a 6-character code appears on screen. Vizio, Samsung, LG, Chromecast, and Apple TV pair automatically when on the same network.
          </p>
          <div className="flex gap-2">
            <input
              value={pairCode}
              onChange={e => setPairCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
              placeholder="ABC123"
              maxLength={6}
              className="flex-1 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white font-mono text-xl text-center focus:outline-none focus:border-violet-500/60 tracking-widest"
              onKeyDown={e => e.key === 'Enter' && claimDevice()}
            />
            <button
              onClick={claimDevice}
              disabled={claiming || pairCode.length !== 6}
              className="px-4 py-2.5 rounded-xl font-bold text-sm text-white disabled:opacity-40 active:scale-95 transition-all"
              style={{ background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)' }}
            >
              {claiming ? '…' : 'Pair'}
            </button>
          </div>
          <p className="text-xs text-neutral-500 mt-2">
            Roku discovered automatically on your Wi-Fi — no code needed if it appears below.
          </p>
        </div>
      )}

      {/* No devices */}
      {!allDevices.length && !showPair && !unpairedVizios.length && (
        <div className="rounded-2xl border border-white/8 bg-[#0A0A0A] p-8 text-center mb-5">
          <Cast className="w-10 h-10 mx-auto mb-3 text-neutral-700" />
          <p className="font-bold text-white mb-1">No TVs connected</p>
          <p className="text-xs text-neutral-500 mb-1 max-w-xs mx-auto">
            Roku is discovered automatically on your Wi-Fi. Fire TV: open the Qude channel and enter the code.
          </p>
          <p className="text-xs text-neutral-600 mb-4 max-w-xs mx-auto">
            Vizio, Samsung, LG, Chromecast, and Apple TV pair automatically on the same network.
          </p>
          <button
            onClick={() => setShowPair(true)}
            className="px-5 py-2.5 rounded-full text-sm font-bold text-white"
            style={{ background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)' }}
          >
            <Plus className="w-4 h-4 inline mr-1.5 -mt-0.5" />Pair your TV
          </button>
        </div>
      )}

      {/* Device picker */}
      {allDevices.length > 0 && (
        <div className="mb-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-600 mb-2">TV</p>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {allDevices.map(d => {
              const active = selectedDevice?.id === d.id;
              const online = d.last_seen_at ? isOnline(d.last_seen_at) : (d.ip ? true : false);
              return (
                <button
                  key={d.id}
                  onClick={() => setSelectedDevice(d)}
                  className="flex-shrink-0 flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold border transition-all active:scale-95"
                  style={active
                    ? { background: 'rgba(124,58,237,0.18)', borderColor: 'rgba(124,58,237,0.5)', color: '#c4b5fd' }
                    : { background: '#111', borderColor: 'rgba(255,255,255,0.1)', color: '#aaa' }}
                >
                  <span className="text-base leading-none">{deviceIcon(d.platform)}</span>
                  <span className="whitespace-nowrap">{d.name}</span>
                  {active && <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#a78bfa' }} />}
                  {online
                    ? <Wifi    className="w-3 h-3 flex-shrink-0 text-green-500" />
                    : <WifiOff className="w-3 h-3 flex-shrink-0 text-neutral-700" />}
                </button>
              );
            })}
          </div>
          {/* Fix 3: Surface active IP so the user can see whether commands have a route */}
          {selectedDevice && (
            <p className="mt-1.5 pl-1 text-[10px] text-neutral-600">
              {selectedDevice.ip
                ? <span className="text-neutral-500">{selectedDevice.ip}</span>
                : <span className="text-yellow-600/70">No IP — d-pad controls unavailable</span>}
            </p>
          )}
          {selectedDevice?.device_id && (
            <button
              onClick={() => unpair(selectedDevice)}
              className="mt-1 text-[10px] text-red-500/50 hover:text-red-400 transition-colors pl-1"
            >
              Remove {selectedDevice.name}
            </button>
          )}
          {selectedDevice && !selectedDevice.device_id && selectedDevice.platform === 'roku' && (
            <button
              onClick={() => ignoreRoku(selectedDevice.ip)}
              className="mt-1 text-[10px] text-red-500/50 hover:text-red-400 transition-colors pl-1"
            >
              Remove {selectedDevice.name}
            </button>
          )}
        </div>
      )}

      {/* Universal remote — d-pad + quick-launch (Roku / Vizio only) */}
      {hasLocalControl && (
        <div className="mb-5 p-4 rounded-2xl border border-white/8 bg-[#0A0A0A]">
          <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-600 mb-4">Remote Control</p>
          <DPad device={selectedDevice} />
          <div className="mt-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-600 mb-2">Open App</p>
            <QuickLaunch device={selectedDevice} onLaunch={launchService} />
          </div>
        </div>
      )}

      {/* Tonight's pick */}
      {tonightPick && (
        <div className="mb-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-600 mb-2">Tonight's pick</p>
          <HeroCard
            card={tonightPick}
            selectedDevice={selectedDevice}
            launching={launching === (tonightPick.show_key || tonightPick.title)}
            onLaunch={() => launch(tonightPick)}
          />
        </div>
      )}

      {/* Show queue */}
      {shows.length > 1 && (
        <div>
          <h3 className="text-[11px] font-black tracking-widest uppercase mb-2" style={{ color: '#4A9EFF' }}>Qude Up</h3>
          <div className="space-y-2">
            {shows.slice(1, 10).map(card => {
              const key = card.show_key || card.title;
              return (
                <ShowRow
                  key={key}
                  card={card}
                  selectedDevice={selectedDevice}
                  launching={launching === key}
                  onLaunch={() => launch(card)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Supported platforms */}
      <button
        onClick={() => setShowPlatforms(v => !v)}
        className="mt-8 w-full flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-neutral-600 hover:text-neutral-400 transition-colors"
      >
        <span>Supported platforms</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPlatforms ? 'rotate-180' : ''}`} />
      </button>
      {showPlatforms && (
        <div className="mt-3 grid grid-cols-2 gap-1.5">
          {[
            ['📺', 'Roku',        'Auto-discovered', null],
            ['📺', 'Vizio',       'Local network', null],
            ['🔥', 'Fire TV',     'Qude app or ADB — tap to connect', '/firetv-adb'],
            ['📺', 'LG',          'Local network', null],
            ['📺', 'Samsung',     'Local network', null],
            ['🎬', 'Chromecast',  'Cast protocol', null],
            ['🍎', 'Apple TV',    'AirPlay', null],
            ['🌐', 'Any browser', 'Deep link', null],
          ].map(([icon, name, note, path]) => (
            <div
              key={name}
              onClick={path ? () => navigate(path) : undefined}
              className={`flex items-center gap-2 p-2.5 rounded-xl bg-[#0A0A0A] border border-white/5 ${path ? 'cursor-pointer hover:border-white/15' : ''}`}
            >
              <span className="text-lg leading-none">{icon}</span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-neutral-300 truncate">{name}</p>
                <p className="text-[10px] text-neutral-600">{note}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── D-pad component ──────────────────────────────────────────────────────────

const DPAD_BTN = {
  position: 'absolute',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(255,255,255,0.1)',
  cursor: 'pointer', userSelect: 'none', touchAction: 'none',
  color: 'rgba(255,255,255,0.85)',
};

function DPad({ device }) {
  const lastSent = useRef({});
  const send = (key) => {
    const now = Date.now();
    if (now - (lastSent.current[key] || 0) < 80) return; // drop accidental double-fires
    lastSent.current[key] = now;
    bridgeSendKey(device, key);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>

      {/* ── Circular D-pad cluster ── */}
      <div style={{
        position: 'relative', width: 204, height: 204,
        borderRadius: '50%',
        background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
        border: '1px solid rgba(255,255,255,0.09)',
        flexShrink: 0,
      }}>
        {/* Up */}
        <button onPointerDown={() => send('up')} className="active:opacity-50 transition-opacity"
          style={{ ...DPAD_BTN, top: 8, left: '50%', transform: 'translateX(-50%)', width: 64, height: 58, borderRadius: '32px 32px 8px 8px' }}>
          <ChevronUp size={22} />
        </button>
        {/* Down */}
        <button onPointerDown={() => send('down')} className="active:opacity-50 transition-opacity"
          style={{ ...DPAD_BTN, bottom: 8, left: '50%', transform: 'translateX(-50%)', width: 64, height: 58, borderRadius: '8px 8px 32px 32px' }}>
          <ChevronDown size={22} />
        </button>
        {/* Left */}
        <button onPointerDown={() => send('left')} className="active:opacity-50 transition-opacity"
          style={{ ...DPAD_BTN, left: 8, top: '50%', transform: 'translateY(-50%)', width: 58, height: 64, borderRadius: '32px 8px 8px 32px' }}>
          <ChevronLeft size={22} />
        </button>
        {/* Right */}
        <button onPointerDown={() => send('right')} className="active:opacity-50 transition-opacity"
          style={{ ...DPAD_BTN, right: 8, top: '50%', transform: 'translateY(-50%)', width: 58, height: 64, borderRadius: '8px 32px 32px 8px' }}>
          <ChevronRight size={22} />
        </button>
        {/* Center OK — violet gradient */}
        <button onPointerDown={() => send('ok')} className="active:opacity-70 transition-opacity"
          style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            width: 64, height: 64, borderRadius: '50%',
            background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)',
            border: '2px solid rgba(167,139,250,0.35)',
            boxShadow: '0 0 22px rgba(124,58,237,0.5)',
            color: '#fff', fontWeight: 900, fontSize: 15,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', userSelect: 'none', touchAction: 'none',
          }}>
          OK
        </button>
      </div>

      {/* ── Back / Home / Power ── */}
      <div style={{ display: 'flex', gap: 10 }}>
        {[
          { key: 'back',  icon: <ArrowLeft size={18} />,  label: 'Back',  color: 'rgba(255,255,255,0.7)' },
          { key: 'home',  icon: <Home size={18} />,       label: 'Home',  color: 'rgba(255,255,255,0.7)' },
          { key: 'power', icon: <Power size={16} />,      label: 'Power', color: '#f87171' },
        ].map(({ key, icon, label, color }) => (
          <button key={key} onPointerDown={() => send(key)} className="active:opacity-50 transition-opacity"
            style={{
              width: 76, height: 52, borderRadius: 14,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
              cursor: 'pointer', userSelect: 'none', touchAction: 'none', color,
            }}>
            {icon}
            <span style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.3)', letterSpacing: 0.3 }}>{label}</span>
          </button>
        ))}
      </div>

      {/* ── Qude Home: always goes to Qude's own Hub, never exits the app.
           Separate from the regular Home button above (which still exits to
           the Roku system Home screen) — no detection, no fallback, one job. ── */}
      <button onPointerDown={() => send('qude_home')} className="active:opacity-50 transition-opacity" aria-label="Qude Home"
        style={{
          width: '100%', height: 44, borderRadius: 14, marginTop: 10,
          background: 'rgba(74,158,255,0.12)',
          border: '1px solid rgba(74,158,255,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', userSelect: 'none', touchAction: 'none',
        }}>
        <img src="/logo.png" alt="Qude Home" style={{ height: 24, width: 24, objectFit: 'contain' }} />
      </button>

      {/* ── Media controls ── */}
      <div style={{ display: 'flex', gap: 8, width: '100%', justifyContent: 'center' }}>
        {[
          { key: 'vol_dn', icon: <Volume1 size={18} />,                         label: 'Vol −' },
          { key: 'mute',   icon: <VolumeX size={18} />,                         label: 'Mute'  },
          { key: 'vol_up', icon: <Volume2 size={18} />,                         label: 'Vol +' },
          { key: 'play',   icon: <Play  size={16} style={{ fill: 'currentColor' }} />, label: 'Play'  },
          { key: 'pause',  icon: <Pause size={16} style={{ fill: 'currentColor' }} />, label: 'Pause' },
        ].map(({ key, icon, label }) => (
          <button key={key} onPointerDown={() => send(key)} className="active:opacity-50 transition-opacity"
            style={{
              flex: 1, height: 50, borderRadius: 12,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
              cursor: 'pointer', userSelect: 'none', touchAction: 'none',
              color: 'rgba(255,255,255,0.75)',
            }}>
            {icon}
            <span style={{ fontSize: 8, fontWeight: 600, color: 'rgba(255,255,255,0.3)', letterSpacing: 0.3 }}>{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Quick-launch service shortcuts ───────────────────────────────────────────

function QuickLaunch({ device, onLaunch }) {
  const visibleApps = QUICK_APPS.filter(app => {
    if (device?.platform === 'vizio') return app.id in VIZIO_APP_IDS;
    if (device?.platform === 'roku')  return app.id in ROKU_APP_IDS;
    return true;
  });
  if (!visibleApps.length) return null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
      {visibleApps.map(app => {
        const logo = LOGO[app.id];
        return (
          <button
            key={app.id}
            onClick={() => onLaunch(app.id)}
            className="active:scale-95 transition-transform"
            style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '10px 4px',
              borderRadius: 16,
              background: logo?.bg || '#111',
              border: `1px solid ${app.color}28`,
              minHeight: 76,
              cursor: 'pointer',
            }}
          >
            {logo ? (
              <img
                src={logo.src}
                alt={app.name}
                draggable={false}
                style={{ width: 34, height: 34, objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <span style={{ fontSize: 18, fontWeight: 900, color: app.color }}>{app.name[0]}</span>
            )}
            <span style={{
              fontSize: 9, fontWeight: 700, lineHeight: 1,
              color: 'rgba(255,255,255,0.45)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              maxWidth: '100%', textAlign: 'center', paddingInline: 2,
            }}>
              {app.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Show cards ───────────────────────────────────────────────────────────────

function HeroCard({ card, selectedDevice, launching, onLaunch }) {
  const service = svc(card.service || card.service_id);
  const ep = card.next_season
    ? epLabel(card.next_season, card.next_episode)
    : epLabel(card.current_season, card.current_episode);

  return (
    <div
      className="relative rounded-2xl overflow-hidden"
      style={{ border: `1px solid ${service.color}30`, minHeight: 180 }}
    >
      {(card.poster_url || card.image_url) ? (
        <>
          <img
            src={(card.poster_url || card.image_url)?.replace('/w300/', '/w780/')}
            alt={card.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)' }} />
        </>
      ) : (
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${service.color}18, #0A0A0A)` }} />
      )}
      <div className="relative p-4 flex flex-col justify-end" style={{ minHeight: 180 }}>
        <span
          className="inline-flex items-center self-start px-2.5 py-1 rounded-full text-[10px] font-bold mb-2"
          style={{ background: `${service.color}22`, color: service.color, border: `1px solid ${service.color}44` }}
        >
          {service.name}
        </span>
        <h2 className="text-xl font-black text-white leading-tight mb-1" style={{ fontFamily: 'Outfit' }}>
          {card.title}
        </h2>
        {card.tonight_reason && (
          <p className="text-[11px] font-medium mb-3" style={{ color: '#a78bfa' }}>✦ {card.tonight_reason}</p>
        )}
        {ep && !card.tonight_reason && (
          <p className="text-xs text-neutral-400 mb-3">{ep}</p>
        )}
        <button
          onClick={onLaunch}
          disabled={launching || !selectedDevice}
          className="self-start flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm text-white disabled:opacity-40 active:scale-95 transition-all"
          style={{ background: selectedDevice ? PLAY_BUTTON_GRADIENT : '#333' }}
        >
          {launching
            ? <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: '#fff4', borderTopColor: '#fff' }} />
            : <><Cast className="w-4 h-4" /> {selectedDevice ? `Play on ${selectedDevice.name}` : 'Select a TV'}</>}
        </button>
      </div>
    </div>
  );
}

function ShowRow({ card, selectedDevice, launching, onLaunch }) {
  const service = svc(card.service || card.service_id);
  const ep = card.next_season
    ? epLabel(card.next_season, card.next_episode)
    : epLabel(card.current_season, card.current_episode);

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-white/6 bg-[#0A0A0A]">
      {card.image_url || card.poster_url
        ? <img
            src={card.image_url || card.poster_url}
            alt={card.title}
            className="w-10 h-14 rounded-lg flex-shrink-0 object-cover"
          />
        : <div
            className="w-10 h-14 rounded-lg flex-shrink-0 flex items-center justify-center"
            style={{ background: `${service.color}1A`, border: `1px solid ${service.color}30` }}
          >
            <Tv className="w-4 h-4" style={{ color: service.color }} />
          </div>
      }
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white truncate">{card.title}</p>
        <p className="text-[11px] text-neutral-500 truncate">
          {service.name}{ep ? ` · ${ep}` : ''}
        </p>
      </div>
      <button
        onClick={onLaunch}
        disabled={launching || !selectedDevice}
        className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-white disabled:opacity-35 active:scale-90 transition-all"
        style={{ background: selectedDevice ? PLAY_BUTTON_GRADIENT : '#222' }}
        title={selectedDevice ? `Play on ${selectedDevice.name}` : 'Select a TV first'}
      >
        {launching
          ? <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: '#fff4', borderTopColor: '#fff' }} />
          : <Cast className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}
